/**
 * Spec 040: pure helpers for the market feed status. No React, no DOM, no
 * network. This file grows in later phases (view model, coverage rows, hint).
 */
import type { QuantFeedRow, QuantFeedView } from '@/ui/QuantFeedIndicator'

import { formatFeedTime } from './feedTime'
import type { DataCoverage, FeedState, FeedStatus, FeedSymbol } from './types'

export const FEED_STATES: readonly FeedState[] = [
  'live',
  'connecting',
  'closed',
  'down',
  'error',
  'unknown',
  'unavailable',
  'not_configured',
]

/** Any value that is not one of the eight known strings becomes `unknown`. */
export function normalizeFeedState(raw: unknown): FeedState {
  return typeof raw === 'string' && (FEED_STATES as readonly string[]).includes(raw) ? (raw as FeedState) : 'unknown'
}

const UNKNOWN_FEED: FeedStatus = {
  state: 'unknown',
  code: null,
  provider_state: null,
  resumes_at: null,
  last_error: null,
  last_trade_at: null,
  last_bar_open_at: null,
  last_bar_written_at: null,
  alive: false,
  catching_up: false,
  symbols: [],
  health: null,
  live_instruments: null,
}

/** A non-object body is read as `{ state: 'unknown' }`; an object is returned typed as it came. */
export function normalizeFeedBody(body: unknown): FeedStatus {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return { ...UNKNOWN_FEED, state: 'unknown' }
  return body as FeedStatus
}

/**
 * What `useQuantFeed` returns. `unavailable` is a first failure with nothing held.
 * `publishingOffStreak` counts consecutive APPLIED answers whose `health.publishing_ok`
 * was not `true` (spec 040 R-4); the publishing row shows from 2.
 */
export type QuantFeedState =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | {
      status: 'loaded'
      feed: FeedStatus
      state: FeedState
      receivedAt: number
      failed: boolean
      publishingOffStreak: number
    }

/**
 * Spec 040 R-4: decides whether an answer is applied or held like a failed read.
 * Only `unavailable` with `code: feed_unreachable` is ever held, and only when an
 * answer is already held (`held`) and it is the first such answer in a row.
 * `unreachableStreak` counts consecutive `feed_unreachable` answers; any other
 * outcome (another answer, or a transport failure) resets it to 0.
 */
export function decideFeedAnswer(args: {
  held: boolean
  unreachableStreak: number
  state: FeedState
  code: unknown
}): { apply: boolean; unreachableStreak: number } {
  const { held, unreachableStreak, state, code } = args
  if (!(state === 'unavailable' && code === 'feed_unreachable')) return { apply: true, unreachableStreak: 0 }
  if (!held) return { apply: true, unreachableStreak: 1 }
  if (unreachableStreak === 0) return { apply: false, unreachableStreak: 1 }
  return { apply: true, unreachableStreak: unreachableStreak + 1 }
}

/** Next publishing-off streak after an applied answer (absent health counts as fine). */
export function nextPublishingOffStreak(previous: number, feed: FeedStatus): number {
  const health = feed.health ?? null
  return health && health.publishing_ok !== true ? previous + 1 : 0
}

/** `<instrument> · <contract>` (or the instrument alone) per symbol, joined by two spaces. */
export function symbolsLabel(symbols: FeedSymbol[] | undefined | null): string {
  if (!symbols) return ''
  return symbols
    .map((s) => (s.contract ? `${s.instrument} · ${s.contract}` : s.instrument))
    .join('  ')
}

export const FEED_UNREADABLE_LINE = { tag: 'warn' as const, msg: 'Market feed status unreadable.' }

/** One terminal line per state change, never per poll. */
export function feedLogLine(args: {
  previous: FeedState | null
  next: FeedState
  symbolsText: string
}): { tag: 'save' | 'warn'; msg: string } | null {
  const { previous, next, symbolsText } = args
  const msg = `Market feed: ${next}${symbolsText === '' ? '' : ` (${symbolsText})`}.`
  if (previous === null) {
    return next === 'down' || next === 'error' ? { tag: 'warn', msg } : null
  }
  if (next === previous) return null
  const warn = next === 'down' || next === 'error' || next === 'unknown' || next === 'unavailable'
  return { tag: warn ? 'warn' : 'save', msg }
}

const WORDS: Record<FeedState, string> = {
  live: 'Live',
  connecting: 'Connecting',
  closed: 'Market closed',
  down: 'Feed down',
  error: 'Provider error',
  unknown: 'Unknown',
  unavailable: 'Status unavailable',
  not_configured: '',
}

const DOTS: Record<FeedState, { tone: QuantFeedView['dotTone']; filled: boolean }> = {
  live: { tone: 'accent', filled: true },
  connecting: { tone: 'accent', filled: false },
  closed: { tone: 'ink-50', filled: true },
  down: { tone: 'alert', filled: true },
  error: { tone: 'alert', filled: true },
  unknown: { tone: 'ink-50', filled: false },
  unavailable: { tone: 'ink-50', filled: false },
  not_configured: { tone: 'ink-50', filled: false },
}

function symbolsDetail(symbols: FeedSymbol[] | undefined | null): string {
  if (!symbols) return ''
  return symbols
    .map((s) => (s.contract ? `${s.symbol} → ${s.instrument} · ${s.contract}` : `${s.symbol} → ${s.instrument}`))
    .join('  ')
}

const note = (key: string, value: string, extra: Partial<QuantFeedRow> = {}): QuantFeedRow => ({
  key,
  label: null,
  value,
  lines: 2,
  ...extra,
})

/** Spec 040: the indicator and panel view model. No colour or word ever derives from `last_trade_at`. */
export function feedView(input: { feed: QuantFeedState; now: number }): QuantFeedView {
  const { feed, now } = input
  if (feed.status === 'loading') {
    return {
      hidden: false,
      interactive: false,
      stateKey: 'checking',
      word: 'Checking',
      dotTone: 'ink-25',
      dotFilled: false,
      symbolsText: '',
      symbolsTitle: '',
      symbolsMuted: false,
      buttonTitle: 'Market feed: Checking',
      announce: 'Feed: Checking',
      lastSeen: false,
      rows: [],
    }
  }
  if (feed.status === 'unavailable') {
    const word = WORDS.unavailable
    return {
      hidden: false,
      interactive: true,
      stateKey: 'unavailable',
      word,
      dotTone: DOTS.unavailable.tone,
      dotFilled: DOTS.unavailable.filled,
      symbolsText: '',
      symbolsTitle: '',
      symbolsMuted: false,
      buttonTitle: `Market feed: ${word}`,
      announce: `Feed: ${word}`,
      lastSeen: false,
      rows: [note('unavailable', 'Cannot read the feed status.')],
    }
  }
  const { feed: body, state } = feed
  if (state === 'not_configured') {
    return {
      hidden: true,
      interactive: true,
      stateKey: state,
      word: '',
      dotTone: 'ink-50',
      dotFilled: false,
      symbolsText: '',
      symbolsTitle: '',
      symbolsMuted: false,
      buttonTitle: '',
      announce: '',
      lastSeen: false,
      rows: [],
    }
  }
  const word = state === 'live' && body.catching_up === true ? 'Live · catching up' : WORDS[state]
  const symbolsText = symbolsLabel(body.symbols)
  const detail = symbolsDetail(body.symbols)
  const health = body.health ?? null
  const rows: QuantFeedRow[] = []
  rows.push({ key: 'state', label: 'State', value: word, muted: body.provider_state ?? undefined })
  if (detail !== '') rows.push({ key: 'symbols', label: 'Symbols', value: detail, title: detail })
  if (body.last_trade_at) {
    rows.push({ key: 'last-trade', label: 'Last trade', value: formatFeedTime(body.last_trade_at, now), title: body.last_trade_at })
  }
  if (body.last_bar_written_at) {
    rows.push({
      key: 'last-bar-written',
      label: 'Last bar written',
      value: formatFeedTime(body.last_bar_written_at, now),
      title: body.last_bar_written_at,
    })
  }
  if (state === 'closed' && body.resumes_at) {
    rows.push({
      key: 'reopens',
      label: 'Reopens',
      value: `Opens ${formatFeedTime(body.resumes_at, now)}`,
      title: body.resumes_at,
    })
  }
  if (health && typeof health.reconnects === 'number') {
    rows.push({ key: 'reconnects', label: 'Reconnects', value: String(health.reconnects) })
  }
  if (health?.completion_pending === true) {
    rows.push({ key: 'completion-pending', label: 'Night completion', value: 'pending' })
  }
  if (health?.completion_refused === true) {
    rows.push(
      note('completion-refused', 'Night completion refused. Backtests cannot use that day until it is fixed.', {
        tone: 'alert',
      }),
    )
  }
  if (health && health.publishing_ok !== true && feed.publishingOffStreak >= 2) {
    rows.push(note('publishing', 'Live publishing is off right now. Recording to disk continues.'))
  }
  if (health?.recording_gap === true) {
    const from = health.trades_unrecoverable_from
    rows.push(
      note(
        'tick-history',
        from
          ? `Live tick history starts ${formatFeedTime(from, now)}. Stored bars are complete.`
          : 'Live tick history is shorter than usual. Stored bars are complete.',
        from ? { title: from } : {},
      ),
    )
  }
  if (state === 'error' && body.last_error) {
    rows.push({ key: 'error', label: 'Error', value: body.last_error, lines: 2, title: body.last_error })
  }
  if ((state === 'unknown' || state === 'unavailable') && body.code) {
    rows.push({ key: 'code', label: 'Code', value: body.code })
  }
  if (feed.failed) {
    rows.push(
      note(
        'check-failed',
        `Last check failed. Showing the answer from ${formatFeedTime(new Date(feed.receivedAt).toISOString(), now)}.`,
      ),
    )
  }
  const down = state === 'down'
  return {
    hidden: false,
    interactive: true,
    stateKey: state,
    word,
    dotTone: DOTS[state].tone,
    dotFilled: DOTS[state].filled,
    symbolsText,
    symbolsTitle: down && detail !== '' ? `last seen: ${detail}` : detail,
    symbolsMuted: down,
    buttonTitle: `Market feed: ${word}${body.provider_state ? ` · provider: ${body.provider_state}` : ''}`,
    announce: `Feed: ${word}`,
    lastSeen: down,
    rows,
  }
}

/**
 * Spec 040: one stored-data row per instrument. The "recording" form needs
 * both `recording === true` and a feed state that says the process is up.
 * `recording_through` and `live_days` are never read.
 */
export function coverageRows(args: {
  coverage: DataCoverage
  feedState: FeedState | null
  feed: FeedStatus | null
  now: number
}): QuantFeedRow[] {
  const { coverage, feedState, feed, now } = args
  const up = feedState === 'live' || feedState === 'connecting' || feedState === 'closed'
  return coverage.instruments.map((row) => {
    const name = row.instrument
    if (row.recording === true && up) {
      const at = feed?.last_bar_written_at
      return {
        key: `cov-${name}`,
        label: null,
        lines: 1,
        value: at ? `${name} · recording, last bar written ${formatFeedTime(at, now)}` : `${name} · recording`,
        title: at ?? undefined,
      }
    }
    return {
      key: `cov-${name}`,
      label: null,
      lines: 1,
      value: row.last_complete_day ? `${name} · through ${row.last_complete_day}` : `${name} · no complete day yet`,
    }
  })
}

/**
 * Spec 040 2.4: the one-line "no live feed" hint for the Config preview.
 * Shown only while the feed process is reported up (`live`, `connecting`,
 * `closed`), the feed lists at least one symbol, and the previewed
 * instrument is not among them. Every other case gives `null`.
 */
export function feedHintLine(args: {
  instrument: string
  feedState: FeedState | null
  symbols: FeedSymbol[] | null | undefined
  dataThrough: string | null
}): string | null {
  const { instrument, feedState, symbols, dataThrough } = args
  if (feedState !== 'live' && feedState !== 'connecting' && feedState !== 'closed') return null
  if (!Array.isArray(symbols) || symbols.length === 0) return null
  if (symbols.some((s) => s.instrument === instrument)) return null
  return dataThrough !== null && dataThrough !== ''
    ? `No live feed for ${instrument}; data through ${dataThrough}.`
    : `No live feed for ${instrument}.`
}
